import { FC } from 'react';
import { Image, Page, Text, View } from '@react-pdf/renderer';
import { styles } from './styles';
import infoIcon from './images/info_icon.png';
import { Tile, TilePageNumber, TilesData, translations } from './interfaces';
import { PageNumberFooter, sideLogo } from './helper';
import { TileCategoriesEnum } from '../DrawerWrapper/tileSummaries';

interface ITilePages {
  tilesData: TilesData[];
  tilePageNumber: TilePageNumber[];
  translations: translations;
}

export const TilePages: FC<ITilePages> = ({
  tilePageNumber,
  translations,
  tilesData
}) => {
  const renderTiles = (tilesData: TilesData) => {
    const pages: React.ReactNode[] = [];

    for (let index = 0; index < tilesData.tiles.length; index += 2) {
      const tile = tilesData.tiles[index];
      const secondTile = tilesData.tiles[index + 1];

      if (tile.isLarge || secondTile?.isLarge) {
        pages.push(
          <TilePage
            translations={translations}
            tile={tile}
            category={tilesData.category}
            tilePageNumber={tilePageNumber}
          />
        );

        secondTile &&
          pages.push(
            <TilePage
              translations={translations}
              tile={secondTile}
              category={tilesData.category}
              tilePageNumber={tilePageNumber}
            />
          );
      } else {
        pages.push(
          <TilePage
            translations={translations}
            tile={tile}
            secondTile={secondTile}
            category={tilesData.category}
            tilePageNumber={tilePageNumber}
          />
        );
      }
    }
    return pages;
  };

  const mainPages: React.ReactNode[] = [];
  tilesData.forEach(toc => {
    mainPages.push(...renderTiles(toc));
  });

  return <>{mainPages}</>;
};

const TilePage: FC<{
  translations: translations;
  tile: Tile;
  secondTile?: Tile;
  category: string | TileCategoriesEnum;
  tilePageNumber: TilePageNumber[];
}> = ({ tile, secondTile, category, tilePageNumber, translations }) => {
  return (
    <Page
      size="A4"
      orientation="landscape"
      style={styles.page}
      key={`page-${tile.id}${secondTile}`}
    >
      <Text style={styles.header}>{category}</Text>
      <Image source={sideLogo(translations.language)} style={styles.logo} />
      <View style={styles.tilesContainer}>
        <TileContainer
          title={tile.title}
          description={tile.description}
          imageSrc={tile.imageSrc}
          id={tile.id}
          tilePageNumber={tilePageNumber}
          isLarge={tile.isLarge}
        />

        {secondTile && (
          <TileContainer
            title={secondTile.title}
            description={secondTile.description}
            imageSrc={secondTile.imageSrc}
            id={secondTile.id}
            tilePageNumber={tilePageNumber}
          />
        )}
      </View>
      <PageNumberFooter />
    </Page>
  );
};

const TileContainer: FC<{
  title: string;
  imageSrc: string;
  description: string;
  id: string;
  tilePageNumber: TilePageNumber[];
  isLarge?: boolean;
}> = ({ title, imageSrc, description, id, tilePageNumber, isLarge }) => {
  return (
    <View style={[styles.tileContainer, { width: isLarge ? '584' : '292' }]}>
      <Image
        id={id}
        source={imageSrc}
        style={{ width: '100%', height: '302.4' }}
      />
      <View style={{ padding: '20px 0 0 0' }}>
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center'
          }}
        >
          <Image
            source={infoIcon}
            style={{ width: '16px', marginRight: 4, padding: 1 }}
          />
          <Text
            style={{ fontSize: '16px', fontWeight: 'bold' }}
            render={({ pageNumber }) => {
              tilePageNumber.push({ id, no: pageNumber });
              return `${title}`;
            }}
          />
        </View>

        <Text style={{ fontSize: '12px', marginTop: 2 }}>{description}</Text>
      </View>
    </View>
  );
};
